import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/auth";
import { Button } from "../../ui/Button";
import { Field } from "../../ui/Field";
import { Input } from "../../ui/Input";

/** E-mail + password, then a TOTP code when the account has two-factor enabled. */
export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"password" | "totp">("password");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: ["me"] });
    await navigate({ to: "/" });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (step === "password") {
        const { data, error } = await authClient.signIn.email({ email, password });
        if (error) return setError(t("auth.invalidCredentials"));
        if (data && "twoFactorRedirect" in data && data.twoFactorRedirect) return setStep("totp");
      } else {
        const { error } = await authClient.twoFactor.verifyTotp({ code });
        if (error) return setError(t("auth.invalidCode"));
      }
      await done();
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="flex min-h-screen items-center justify-center bg-zinc-950 p-4">
      <form
        onSubmit={submit}
        className="flex w-full max-w-sm flex-col gap-4 rounded-lg border border-zinc-800 bg-zinc-900 p-6"
      >
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Ratun</h1>
          <p className="text-sm text-zinc-400">{t("auth.subtitle")}</p>
        </div>
        {step === "password" ? (
          <>
            <Field label={t("auth.email")}>
              <Input
                type="email"
                autoComplete="username"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </Field>
            <Field label={t("auth.password")}>
              <Input
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </Field>
          </>
        ) : (
          <Field label={t("auth.totpCode")} hint={t("auth.totpHint")}>
            <Input
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
          </Field>
        )}
        {error && <p className="text-sm text-red-400">{error}</p>}
        <Button type="submit" variant="primary" disabled={busy}>
          {t("auth.signIn")}
        </Button>
      </form>
    </main>
  );
}
